import 'react-phone-number-input/style.css';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Modal,
} from 'react-bootstrap';
import _ from 'lodash';
import PhoneInput, { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import * as Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { useAlert } from 'react-alert';

import {
  GQL_CREATE_DIALOG,
  GQL_GET_ME,
  GQL_GET_SETTING,
  GQL_SEND_VIEWING_ROOM_REQUEST,
} from '../../graphql/queries';
import { ACTIVE, ERROR } from '../../helpers/colors';
import { LANG_RU } from '../../locales';
import { ArtField } from '../forms';
import { useTranslation } from '../hooks/useTranslation';
import ModalCloseButton from '../ModlaCloseButton';

const ViewingRoomModal = ({ show, onHide, model }) => {
  const client = useApolloClient();
  const history = useHistory();
  const { t } = useTranslation();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [hide, hideContent] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: '',
    phone: '',
    message: '',
  });

  const { data: dataSetting } = useQuery(GQL_GET_SETTING, {
    variables: { code: 'moderator_id' },
  });

  const data = client.readQuery({ query: GQL_GET_ME });

  const me = _.get(data, 'me', null);

  const companion_id = _.get(dataSetting, 'setting.value', null);

  const [sendRequest] = useMutation(GQL_SEND_VIEWING_ROOM_REQUEST);

  const [dialogProcess] = useMutation(GQL_CREATE_DIALOG);

  const reset = () => setInitialValues({
    name: '',
    phone: '',
    message: '',
  });

  useEffect(
    () => () => {
      reset();
    },
    [],
  );

  const contactModerator = async () => {
    if (!companion_id) {
      return false;
    }
    try {
      await dialogProcess({
        variables: { companion_id },
        fetchPolicy: 'no-cache',
      }).then((response) => {
        if (_.get(response, 'data.createDialog._id')) {
          history.push({
            pathname: `/account/messages/${_.get(response, 'data.createDialog._id')}`,
            state: {
              message: t('hello_moderator_name', { ns: 'viewingRoom', name: _.get(model, 'name', '') }),
            },
          });
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
    return true;
  };

  const handleUpdate = async (val, { setSubmitting, setErrors }) => {
    try {
      setLoading(true);
      const variables = _.pick(val, ['name', 'phone', 'message']);
      variables.room_id = _.get(model, '_id');
      const response = await sendRequest({ variables });
      const errors = _.get(response, 'errors.[0].extensions', {});
      if (!_.isEmpty(errors)) {
        setSubmitting(false);
        setErrors(errors);
      } else {
        hideContent(true);
        reset();
        alert.show(t('send_success', { ns: 'viewingRoom' }), { type: 'success' });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
    setLoading(false);
  };

  const validatePhone = (phone) => (phone ? isPossiblePhoneNumber(phone) && isValidPhoneNumber(phone) : true);

  const validationSchema = !_.get(me, '_id', null)
    ? Yup.object({
      name: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .nullable(),
      phone: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .min(5, t('minimum_length', { ns: 'validate', count: 5 }))
        .max(20, t('maximum_length', { ns: 'validate', count: 20 }))
        .nullable(),
      message: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .nullable(),
    })
    : Yup.object({
      message: Yup.string(t('only_text', { ns: 'validate' }))
        .required(t('required', { ns: 'validate' }))
        .nullable(),
    });

  return (
    <Modal size="cs" show={show} onHide={onHide} backdrop="static" centered>
      <div className="p-2 p-md-5">
        <ModalHeader>
          <CardName>{t('add', { ns: 'viewingRoom', name: _.get(model, 'name', '') })}</CardName>
          <ModalCloseButton onHide={onHide} loading={loading} />
        </ModalHeader>
        {!hide ? (
          <Modal.Body className="p-2 p-md-0">
            <Formik
              initialValues={initialValues}
              onSubmit={handleUpdate}
              validationSchema={validationSchema}
              enableReinitialize
            >
              {({
                isSubmitting, setFieldValue, values, errors,
              }) => (
                <FormStyle>
                  {!_.get(me, '_id', null) && (
                  <>
                    <Area>
                      <H4>{t('name', { ns: 'viewingRoom' })}</H4>
                      <Field
                        name="name"
                        disabled={loading}
                        className="form-control-lg"
                        placeholder={t('name_placeholder', { ns: 'viewingRoom' })}
                        column
                        component={ArtField}
                      />
                    </Area>
                    <Area>
                      <H4>{t('phone', { ns: 'viewingRoom' })}</H4>
                      <PhoneInput
                        international
                        name="phone"
                        required
                        className={`form-control-lg ${
                          _.get(errors, 'phone') || !validatePhone(values.phone) ? 'is-invalid' : ''
                        }`}
                        value={values.phone}
                        placeholder={t('phone_placeholder', { ns: 'viewingRoom' })}
                        defaultCountry={LANG_RU.toUpperCase()}
                        onChange={(val) => setFieldValue('phone', val)}
                      />
                      {_.get(errors, 'phone') && (<Error>{_.get(errors, 'phone')}</Error>)}
                    </Area>
                  </>
                  )}
                  <Area>
                    <H4>{t('message', { ns: 'viewingRoom' })}</H4>
                    <Field
                      name="message"
                      disabled={loading}
                      className={`form-control-lg w-100 ${_.get(errors, 'message') ? 'is-invalid' : ''}`}
                      placeholder={t('message_placeholder', { ns: 'viewingRoom' })}
                      column
                      as="textarea"
                    />
                    {_.get(errors, 'message') && (
                      <Error>
                        {_.get(errors, 'message')}
                      </Error>
                    )}
                  </Area>
                  <Footer>
                    <Agreement>
                      {t('agreement', { ns: 'viewingRoom' })}
                      <a rel="noreferrer nofollow noopener" target="_blank" href="/privacy-policy">
                        {t('agreement_link', { ns: 'viewingRoom' })}
                      </a>
                    </Agreement>
                    <Buttons>
                      {companion_id && _.get(me, '_id', null) && (
                        <Button
                          type="button"
                          variant="outline-primary"
                          size="lg"
                          onClick={contactModerator}
                          disabled={isSubmitting || loading}
                        >
                          {t('contact_moderator', { ns: 'action' })}
                        </Button>
                      )}
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={isSubmitting || loading}
                      >
                        {isSubmitting ? t('await', { ns: 'action' }) : t('send', { ns: 'action' })}
                      </Button>
                    </Buttons>
                  </Footer>
                </FormStyle>
              )}
            </Formik>
          </Modal.Body>
        ) : (
          <Modal.Body className="p-2 p-md-0">
            {!_.get(me, '_id', null)
              ? t('thanks', { ns: 'viewingRoom' })
              : t('thanks_user', { ns: 'viewingRoom', name: _.get(me, 'name', '') })}
          </Modal.Body>
        )}
      </div>
    </Modal>
  );
};

const ModalHeader = styled.div`
  display: flex;
  padding: 1rem;
  width: 100%;
  justify-content: space-between;
  @media screen and (max-width: 500px) {
    flex-direction: column-reverse;
    justify-content: flex-end;
    align-items: flex-end;
  }
  
`;

const Error = styled.div`
  margin-top: 1.25rem;
  font-size: .875em;
  color: ${ERROR};
`;

const Area = styled.div`
  margin-bottom: 40px;
  .invalid-feedback {
    color: ${ERROR};
  }
  .is-invalid {
    border-color: ${ERROR};
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media screen and (max-width: 991px) {
    flex-direction: column-reverse;
  }
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  button {
    margin-left: 20px;
  }
  @media screen and (max-width: 991px) {
    flex-direction: column;
    button {
      width: 100%;
      margin-left: 0;
      margin-bottom: 20px;
    }
  }
  @media screen and (max-width: 500px) {
    width: 100%;
  }
`;

const Agreement = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 130%;
  a {
    display: block;
    color: ${ACTIVE};
    font-weight: 400;
    font-size: 16px;
    line-height: 130%;
    &:hover, &:focus {
      text-decoration: underline;
    }
  }
`;

const H4 = styled.h4`
  font-weight: 700;
  font-size: 24px;
  line-height: 120%;
  margin-bottom: 20px;
  @media screen and (max-width: 500px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export const RadioLabel = styled.label`
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  font-size: 20px;
  margin-bottom: 0;
  .check {
    margin-right: 20px;
  }
  @media screen and (max-width: 1170px) {
    font-size: 80%;
  }
  @media screen and (max-width: 768px) {
    font-size: 90%;
  }
  @media screen and (max-width: 500px) {
    font-size: 80%;
    margin-bottom: 10px;
  }
`;

const FormStyle = styled(Form)`
  .PhoneInputInput {
    border: 0;
  }
  @media screen and (max-width: 768px) {
    .form-control {
      padding: 1rem 19px;
      height: calc(1.5em + 0.75rem + 20px);
    }
  }
`;

const CardName = styled.div`
  font-weight: bold;
  font-size: 40px;
  line-height: 120%;
  margin-bottom: 20px;
  @media screen and (max-width: 1024px) {
    font-size: 32px;
    text-align: center;
  }
  @media screen and (max-width: 768px) {
    font-size: 26px;
    text-align: left;
  }
`;

ViewingRoomModal.defaultProps = {
  show: false,
};

ViewingRoomModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  model: PropTypes.object.isRequired,
};

export default ViewingRoomModal;
